import {
    ActionRecord,
    MetaEntry,
    ReviewResponse,
    DataResponse,
    WorkflowStep,
    WorkflowConfig,
    DuplicateRow,
} from '../../lib/types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { data as dataApi, tasks } from '../../lib/api'
import Loading from '../../components/loading'
import Table from '../../components/table'
import Pagination from '../../components/pagination'
import calculateStats from './stats'
import StatsInfo from './stats-info'
import ViewType from '../../components/view-type'
import ErrorMsg from '../../components/error-msg'
import getId from '../../lib/get-id'
import filterReviews from './filter-review'
import { findRowMeta, getWorkflowConfig } from '../../workflows/util'
import { Search } from '@carbon/react'
import { useDebouncedCallback } from 'use-debounce'

type Props = {
    step: WorkflowStep
    viewType: string
    setViewType: (viewType: string) => void
    page: number
    setPage: (page: number) => void
}

export default function ReviewStep(props: Props) {
    const { step, viewType, setViewType, page, setPage } = props
    const { id } = useParams()
    const [config, setConfig] = useState<WorkflowConfig>()
    const [err, setErr] = useState('')

    useEffect(() => {
        getWorkflowConfig(Number(id))
            .then(setConfig)
            .catch((err) => setErr(err.message || err))
    }, [id])

    let prevStep = config?.steps[0]

    let loadData = dataApi.page

    let fields = prevStep?.view?.elements?.map((e) => e.field!).filter((_) => _)

    if (err) return <ErrorMsg error={err} />
    return config ? (
        <ReviewSummary
            step={step}
            loadData={loadData}
            fields={fields}
            idField={prevStep?.entityType?.idField}
            viewType={viewType}
            setViewType={setViewType}
            page={page}
            setPage={setPage}
        />
    ) : (
        <Loading />
    )
}

type ReviewProps = {
    step: WorkflowStep
    loadData: (
        id: number,
        stepIndex: number,
        page: number,
        viewType: string,
        search: string,
    ) => Promise<DataResponse>
    fields?: Array<string>
    idField?: string
    viewType: string
    setViewType: (viewType: string) => void
    page: number
    setPage: (page: number) => void
}

export function ReviewSummary(props: ReviewProps) {
    const { step, loadData, idField, fields, viewType, setViewType, page, setPage } = props
    const { id, stepIndex } = useParams()
    //const [page, setPage] = useState(0);
    const [search, setSearch] = useState('')
    const [rows, setRows] = useState<Array<any>>([])
    const [meta, setMeta] = useState<Array<MetaEntry>>([])
    const [total, setTotal] = useState(0)
    const [action, setAction] = useState<ActionRecord>()
    const [reviews, setReviews] = useState<Array<ActionRecord>>([])
    const [loading, setLoading] = useState(true)
    const [finishing, setFinishing] = useState(false)
    const [err, setErr] = useState('')
    const [duplicates, setDuplicates] = useState<DuplicateRow[]>([])
    const navigate = useNavigate()

    useEffect(() => {
        setLoading(true)
        Promise.all([
            loadData(Number(id), Number(stepIndex), page, viewType, search),
            dataApi.getDuplicates(Number(id)),
        ])
            .then(([dataResponse, dupResponse]) => {
                const { action, reviews, rows, meta, total } = dataResponse as ReviewResponse
                const { duplicates } = dupResponse
                const filteredReviews = filterReviews(reviews, meta)
                if (!!idField && rows.length && !rows[0].id) {
                    setRows(rows.map((row) => ({ id: row[idField], ...row })))
                } else {
                    setRows(rows)
                }
                setMeta(meta)
                setTotal(total)
                setAction(action)
                setReviews(filteredReviews)
                setLoading(false)
                setDuplicates(duplicates)
            })
            .catch((err) => {
                setErr(err.message || err)
                setLoading(false)
            })
    }, [id, stepIndex, page, viewType, idField, loadData, search])

    const handleSearch = useDebouncedCallback((value: string) => {
        setLoading(true)
        setSearch(value)
        setPage(0)
    }, 2000)

    const indicatorFn = useCallback(
        (id: number, meta: MetaEntry) => {
            if (reviews.find((r) => r.extras && JSON.parse(r.extras).objId === id)) return 'blue'
            if (meta.error) return 'red'
            if (
                duplicates.find(
                    (d) => d.objId === id && (!d.cycleId || d.cycleId === meta.correctionCycleId),
                )
            )
                return 'yellow'
            return ''
        },
        [reviews, duplicates],
    )

    const stats = useMemo(() => calculateStats(total, reviews), [total, reviews])

    const reviewRow = (row: any, modKey: boolean) => {
        let rowMeta = findRowMeta(meta, row)
        let path = `/workflows/${id}/steps/${stepIndex}/review/${rowMeta!.accountId}/${getId(row)}`
        if (modKey) window.open(window.location.origin + path, '_blank')
        else navigate(path)
    }

    let actionName = step.type === 'review' ? 'Merge' : 'Approve'
    async function finish() {
        setFinishing(true)
        await tasks.finish(Number(id), Number(stepIndex))
        alert(`Data ${actionName}d!`)
        window.location.reload()
    }

    return (
        <>
            {err && <ErrorMsg error={err} />}
            <div className='flex flex-row justify-between items-center'>
                <ViewType value={viewType} onChange={setViewType} changeName='Reviewed' />
                <span className='bg-black'>
                    <Search
                        size='sm'
                        placeholder='Search'
                        labelText='Search'
                        style={{ borderRadius: 0, boxShadow: 'initial' }}
                        closeButtonLabelText='Clear search input'
                        id='workflow-search'
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </span>
            </div>
            {loading ? (
                <Loading />
            ) : (
                <>
                    {stats && (
                        <div className='flex items-center'>
                            <div className='w-1/2 pr-5'>
                                <StatsInfo
                                    stats={stats}
                                    action={action}
                                    actionName={actionName + 'd'}
                                />
                            </div>
                            <div className='w-1/2 pb-3'>
                                <div className='text-sm text-gray-500 mb-2'>
                                    {step.percentCoverage}% review coverage is required to finish.
                                    Note that flagged errors are not included in the finished data.
                                </div>
                                <button
                                    className='orange'
                                    disabled={
                                        finishing ||
                                        stats.approvalsPct < Number(step.percentCoverage)
                                    }
                                    onClick={finish}
                                >
                                    {finishing ? 'Finishing...' : `Finish and ${actionName} Data`}
                                </button>
                            </div>
                        </div>
                    )}

                    {rows.length ? (
                        <Table
                            data={rows}
                            meta={meta}
                            indicatorFn={indicatorFn}
                            onClick={reviewRow}
                            fields={fields}
                        />
                    ) : (
                        <em>No Changes Made</em>
                    )}
                    <Pagination page={page} total={total} onChange={setPage} />
                </>
            )}
        </>
    )
}
